/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { Fragment, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, Search, Layers, ChevronRight, Home, Tag } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GestioneServizi() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [groupByCategory, setGroupByCategory] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/services?active_only=false&include_prices=true`);
      setServices(res.data);
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/services/${id}`, {});
      toast.success('Servizio disattivato');
      fetchData();
    } catch {
      toast.error('Errore nella disattivazione');
    }
  };

  const handleToggleHome = async (s, value) => {
    // Aggiornamento ottimistico
    setServices(prev => prev.map(x => x.id === s.id ? { ...x, home_page: value } : x));
    try {
      await axios.put(`${API}/services/${s.id}`, { home_page: value }, {});
      toast.success(value ? `"${s.nome}" mostrato in Home Page` : `"${s.nome}" rimosso dalla Home Page`);
    } catch {
      setServices(prev => prev.map(x => x.id === s.id ? { ...x, home_page: !value } : x));
      toast.error('Errore nell\'aggiornamento del flag Home Page');
    }
  };

  const handleTogglePromo = async (s, value) => {
    setServices(prev => prev.map(x => x.id === s.id ? { ...x, promozione: value, promo_attiva: value && (!x.promo_scadenza || x.promo_scadenza >= todayStr) } : x));
    try {
      await axios.put(`${API}/services/${s.id}`, { promozione: value }, {});
      toast.success(value ? `"${s.nome}" in promozione` : `Promozione rimossa da "${s.nome}"`);
    } catch {
      setServices(prev => prev.map(x => x.id === s.id ? { ...x, promozione: !value } : x));
      toast.error('Errore nell\'aggiornamento della promozione');
    }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

  const homeCount = services.filter(s => s.home_page && s.active).length;
  const promoCount = services.filter(s => s.promo_attiva).length;

  const categories = [...new Set(services.map(s => s.categoria))].filter(Boolean).sort((a, b) => a.localeCompare(b, 'it'));

  const filteredServices = services.filter(s => {
    if (categoryFilter !== 'all' && s.categoria !== categoryFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.nome || '').toLowerCase().includes(q)
      || (s.categoria || '').toLowerCase().includes(q)
      || (s.descrizione || '').toLowerCase().includes(q);
  });

  const groupedServices = filteredServices.reduce((acc, s) => {
    const cat = s.categoria || 'Senza tipologia';
    (acc[cat] = acc[cat] || []).push(s);
    return acc;
  }, {});
  const sortedGroups = Object.keys(groupedServices).sort((a, b) => a.localeCompare(b, 'it'));

  const renderServiceRow = (s) => (
    <TableRow
      key={s.id}
      data-testid="service-row"
      onClick={() => navigate(`/backoffice/servizi/${s.id}`)}
      className="cursor-pointer hover:bg-[hsl(var(--secondary))] transition-colors duration-150"
    >
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="truncate">{s.nome}</span>
        </div>
      </TableCell>
      <TableCell><Badge variant="secondary">{s.categoria}</Badge></TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Clock size={14} className="text-muted-foreground" /> {s.durata_minuti} min
        </div>
      </TableCell>
      <TableCell>
        {s.prezzo ? `${s.prezzo.toFixed(2)} EUR` : 'N/D'}
        {s.pagamento_online === 'full' && <Badge variant="secondary" className="ml-2 bg-teal-100 text-teal-800">Saldo online</Badge>}
        {s.pagamento_online === 'deposit' && <Badge variant="secondary" className="ml-2 bg-teal-100 text-teal-800">Acconto {Number(s.acconto_importo || 0).toFixed(2)} EUR</Badge>}
      </TableCell>
      <TableCell><Badge variant={s.active ? 'default' : 'secondary'} className={s.active ? 'bg-green-100 text-green-800' : ''}>{s.active ? 'Attivo' : 'Inattivo'}</Badge></TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <Switch
            checked={!!s.home_page}
            onCheckedChange={v => handleToggleHome(s, v)}
            disabled={user?.role !== 'admin'}
            aria-label={`Mostra ${s.nome} in Home Page`}
            data-testid={`service-home-switch-${s.id}`}
          />
        </div>
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <div className="flex flex-col items-center gap-1">
          <Switch
            checked={!!s.promozione}
            onCheckedChange={v => handleTogglePromo(s, v)}
            disabled={user?.role !== 'admin'}
            aria-label={`Promozione ${s.nome}`}
            data-testid={`service-promo-switch-${s.id}`}
          />
          {s.promozione && s.promo_scadenza && (
            <span className={`text-[10px] leading-none ${s.promo_scadenza >= todayStr ? 'text-muted-foreground' : 'text-red-600 font-medium'}`} data-testid={`service-promo-scadenza-${s.id}`}>
              {s.promo_scadenza >= todayStr ? `fino al ${formatDate(s.promo_scadenza)}` : 'scaduta'}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
          {s.active && user?.role === 'admin' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-red-600" data-testid={`service-delete-btn-${s.id}`}><Trash2 size={14} /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disattiva Servizio</AlertDialogTitle>
                  <AlertDialogDescription>Sei sicuro di voler disattivare "{s.nome}"? Il servizio non sara piu disponibile per nuove prenotazioni.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleDelete(s.id)} className="bg-red-600">Disattiva</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead className="w-[25%]">Nome</TableHead>
        <TableHead className="w-[14%]">Categoria</TableHead>
        <TableHead className="w-[10%]">Durata</TableHead>
        <TableHead className="w-[20%]">Prezzo</TableHead>
        <TableHead className="w-[9%]">Stato</TableHead>
        <TableHead className="w-[8%] text-center">
          <span className="inline-flex items-center gap-1" title={`${homeCount} servizi mostrati in "I Nostri Servizi"`}><Home size={13} /> Home <span className="text-[#238A92] font-semibold" data-testid="services-home-count">({homeCount})</span></span>
        </TableHead>
        <TableHead className="w-[8%] text-center">
          <span className="inline-flex items-center gap-1" title={`${promoCount} promozioni attive in Home Page`}><Tag size={13} /> Promo <span className="text-[#238A92] font-semibold" data-testid="services-promo-count">({promoCount})</span></span>
        </TableHead>
        <TableHead className="w-[6%] text-right">Azioni</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <PageTitle title="Gestione Servizi" info="Aggiungi, modifica o disattiva i servizi medici. Clicca su una riga per modificare. Gli switch Home e Promo controllano cosa compare in Home Page; il numero accanto all'intestazione indica quanti servizi sono attivi in ciascuna sezione." />
        </div>
        {user?.role === 'admin' && (
          <Button onClick={() => navigate('/backoffice/servizi/nuovo')} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="service-new-btn">
            <Plus size={16} className="mr-1" /> Nuovo Servizio
          </Button>
        )}
      </div>

      {/* Toolbar: ricerca + filtro tipologia + raggruppamento */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cerca servizio per nome, tipologia o descrizione..."
                className="pl-8"
                data-testid="services-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-56" data-testid="services-category-filter">
                <SelectValue placeholder="Tutte le tipologie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le tipologie</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Layers size={14} className="text-muted-foreground" />
              <Label htmlFor="group-switch" className="text-sm cursor-pointer">Raggruppa per tipologia</Label>
              <Switch id="group-switch" checked={groupByCategory} onCheckedChange={setGroupByCategory} data-testid="services-group-switch" />
            </div>
          </div>
          {(search.trim() || categoryFilter !== 'all') && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="services-filter-count">
              {filteredServices.length} {filteredServices.length === 1 ? 'servizio trovato' : 'servizi trovati'}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : filteredServices.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center" data-testid="services-empty">
              {search.trim() || categoryFilter !== 'all' ? 'Nessun servizio corrisponde alla ricerca' : 'Nessun servizio presente'}
            </p>
          ) : groupByCategory ? (
            <div className="overflow-x-auto">
              <Table className="table-fixed">
                {tableHeader}
                <TableBody>
                  {sortedGroups.map(cat => (
                    <Fragment key={cat}>
                      <TableRow className="hover:bg-transparent" data-testid={`service-group-${cat}`}>
                        <TableCell colSpan={8} className="bg-[hsl(var(--secondary))] py-2.5">
                          <div className="flex items-center gap-2">
                            <Layers size={14} className="text-[#238A92]" />
                            <span className="font-semibold text-sm text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}}>{cat}</span>
                            <Badge variant="outline" className="text-[10px]">{groupedServices[cat].length}</Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                      {groupedServices[cat].map(renderServiceRow)}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-fixed">
                {tableHeader}
                <TableBody>
                  {filteredServices.map(renderServiceRow)}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
